import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowUpRight } from "lucide-react";
import api from "../lib/api";
import { Reveal } from "../components/Reveal";
import ServiceIcon from "../components/ServiceIcon";
import { usePageMeta } from "../lib/seo";

export default function Services() {
  usePageMeta({
    title: "AI Services",
    description:
      "Generative AI & RAG systems, AI chatbots & agents, n8n workflow automation, data engineering, cloud & DevOps, and custom software — full-stack AI engineering services.",
    path: "/services",
  });
  const [services, setServices] = useState([]);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="services-page">
      <section className="relative grid-bg border-b border-white/10">
        <div className="absolute inset-0 radial-glow pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 pt-36 pb-20 sm:pt-44">
          <Reveal>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-primary mb-4">Our Services</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold max-w-3xl leading-[1.07]">
              AI capabilities that move the needle.
            </h1>
            <p className="mt-6 text-slate-400 max-w-2xl text-lg leading-relaxed">
              From generative AI and automation to data engineering and cloud — we deliver the full stack
              of modern AI engineering, tailored to your business.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-20 sm:py-28 space-y-6">
        {services.map((s, i) => (
          <Reveal key={s.slug} delay={(i % 2) * 80}>
            <div data-testid={`service-card-${s.slug}`} className="group grid lg:grid-cols-12 gap-8 border border-white/10 bg-[#0F111A] p-8 sm:p-10 hover:border-primary/40 transition-all duration-300">
              <div className="lg:col-span-5">
                <div className="w-14 h-14 grid place-items-center border border-primary/30 bg-primary/10 text-primary mb-6 group-hover:glow-blue transition-all">
                  <ServiceIcon name={s.icon} className="w-7 h-7" />
                </div>
                <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-white">{s.title}</h2>
                <p className="text-primary text-sm font-medium mt-2">{s.tagline}</p>
                <p className="text-slate-400 mt-5 leading-relaxed">{s.description}</p>
              </div>
              <div className="lg:col-span-7 lg:border-l lg:border-white/10 lg:pl-10 flex flex-col justify-center">
                <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-5">What's included</p>
                <ul className="grid sm:grid-cols-2 gap-4">
                  {s.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-3 text-slate-200">
                      <span className="w-5 h-5 grid place-items-center bg-primary/15 text-primary shrink-0 mt-0.5">
                        <Check className="w-3.5 h-3.5" />
                      </span>
                      <span className="text-sm leading-relaxed">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Reveal>
        ))}
      </section>

      <section className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-20 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold">Not sure where to start?</h2>
          <p className="mt-4 text-slate-400 max-w-xl mx-auto">Book a free consultation and we'll recommend the right AI approach for your goals.</p>
          <Link to="/contact" className="inline-flex items-center gap-2 mt-8 bg-primary hover:bg-primary/90 text-white font-semibold px-8 py-4 glow-blue transition-all">
            Talk to an Expert <ArrowUpRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
